import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation } from '@reach/router';
import styled from 'styled-components';
import { Context } from '@components/Context';
import { NavContext } from '@components/navigation/Context';
import Colors from '@components/shared/Colors';
import LinkTranslated from '@components/shared/LinkTranslated';
import useDesktop from '@components/shared/useDesktop';
import { HAMBURGER_BREAKPOINT } from '@constants';
import { NavStatus } from '@contracts';
import DayeLogo from '@images/daye-logo.svg';
import { calculatePaidCartItemsCount } from '@utils/cart';
import { pageIsAccountPage } from '@utils/helpers';
import { removeLocalesFromPath } from '@utils/language';
import useBlockScroll from '@utils/useBlockScroll';
import NavBarLeft from './NavBarLeft';
import NavBarRight from './NavBarRight';

const NavBar: React.FC = () => {
  const { state, dispatch } = useContext(Context);
  const { navState, navDispatch } = useContext(NavContext);
  const { cartItems, selectedBox, selectedPlan, loggedIn } = state;
  const { status, color, showingBanner, navbarColor, logo } = navState;
  const [animate, setAnimate] = useState(false);
  const [navItemColor, setNavItemColor] = useState(Colors.forestGreen);
  const desktop = useDesktop(1024);
  const isBasketMobile = !useDesktop(768);
  const location = useLocation();
  const blockScroll = useBlockScroll();
  const currentNavColor = desktop ? navItemColor : navItemColor;
  const isCheckoutPage = location.pathname.includes('/checkout/');
  const isVMSLandingPage = location.pathname.includes('/at-home-infection-screening') || location.pathname.includes('/at-home-fertility-screening');
  const isPressPage = location.pathname.includes('/press/');
  const isAccountPage = pageIsAccountPage(location.pathname);
  const isSticky = !isCheckoutPage && !isVMSLandingPage && !isPressPage;

  useEffect(() => {
    setNavItemColor(color);
  }, [color]);

  useEffect(() => {
    if (cartItems.length > 0) {
      const basketCount = calculatePaidCartItemsCount(cartItems);
      navDispatch({ type: 'setNavbarBasketCount', data: basketCount });
    } else {
      navDispatch({ type: 'setNavbarBasketCount', data: 0 });
    }
  }, [cartItems, selectedPlan, selectedBox]);

  useEffect(() => {
    if (!isCheckoutPage) {
      dispatch({ type: 'setTax', data: undefined });
    }

    window.addEventListener('scroll', animateScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', animateScroll);
      blockScroll(false);
    };
  }, []);

  useEffect(() => {
    const block = status !== NavStatus.Default;
    blockScroll(block);
  }, [status]);

  const animateScroll = () => {
    if (window.scrollY > 30 && isSticky) {
      setAnimate(true);
      return;
    }

    setAnimate(false);
  };

  const styleOptions = useMemo(() => {
    const options: { top: number; backgroundColor?: string } = { top: 0 };

    if (isVMSLandingPage) {
      options.backgroundColor = Colors.sky;
    }

    if (showingBanner && !animate && !(status === NavStatus.Basket && isBasketMobile)) {
      options.top = 50;
    }

    return options;
  }, [isVMSLandingPage, showingBanner, animate, status, isBasketMobile]);

  function getLogoToLink() {
    if (loggedIn && isAccountPage && removeLocalesFromPath(location.pathname) !== '/dashboard/') {
      return '/dashboard/';
    }

    return '/';
  }

  return (
    <Container
      id="nav-bar"
      $sticky={isSticky}
      $noIndex={isVMSLandingPage}
      $animate={animate}
      style={styleOptions}
      $color={currentNavColor}
      $navbarColor={navbarColor}
      $sideContainersHidden={false}
      $status={status}
    >
      <LeftContainer>
        <NavBarLeft />
      </LeftContainer>
      <LogoLink to={getLogoToLink()} $hideOnScroll={false} aria-label="Go to Daye homepage">
        {logo ?? <DayeLogo />}
      </LogoLink>
      <RightContainer>
        <NavBarRight />
      </RightContainer>
    </Container>
  );
};

export default NavBar;

interface IContainer {
  $color: string;
  $sticky: boolean;
  $animate: boolean;
  $navbarColor: string;
  $sideContainersHidden: boolean;
  $noIndex: boolean;
  $status: NavStatus;
}

const Container = styled.div<IContainer>`
  position: ${(props) => (props.$sticky ? 'fixed' : 'relative')};
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: ${(props) => (props.$sideContainersHidden ? 'center' : 'space-between')};
  ${(props) => (props.$noIndex ? '' : 'z-index: 101;')};
  margin-top: 0;
  transition: padding 0.4s ease, background-color 0.4s ease, top 0.2s;
  background-color: transparent;
  padding: 15px 60px;

  ${(props) => {
    if (props.$animate) {
      return `
        background-color: ${props.$navbarColor};
        ${props.$navbarColor !== 'transparent' && props.$status !== NavStatus.Menu && 'box-shadow: rgba(0, 0, 0, 0.06) 0 2px 20px 0;'}
      `;
    }

    return '';
  }};

  @media (max-width: ${HAMBURGER_BREAKPOINT + 250}px) {
    padding: 15px 30px;
  }

  @media (max-width: ${HAMBURGER_BREAKPOINT}px) {
    border-bottom: none;
  }

  @media (max-width: 768px) {
    padding: 20px 25px;
    margin-bottom: 0;
  }
`;

const LeftContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  width: 520px;
  z-index: 102;

  @media (max-width: 768px) {
    gap: 15px;
    width: 140px;
  }
`;

const LogoLink = styled(LinkTranslated)<{ $hideOnScroll?: boolean }>`
  display: flex;
  transition: 0.2s all ease;
  z-index: 102;
  position: relative;
  top: 3px;
  padding: 2.5px 5px;
  width: 158px;
  align-items: center;
  justify-content: center;

  & svg {
    width: 100%;
    height: 35px;

    @media (max-width: 768px) {
      height: 30px;
    }

    & > path {
      fill: ${Colors.navyBlue};
    }
  }

  &:hover {
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    padding: 0 5px;
  }

  @media (min-width: 768px) {
    opacity: ${(props) => (props.$hideOnScroll ? 0 : 1)};
    transition: opacity 0.3s ease;
  }
`;

const RightContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 25px;
  width: 520px;
  z-index: 102;

  @media (max-width: 768px) {
    width: 140px;
    gap: 20px;
  }
`;
